<div class="block w-full overflow-hidden rounded-[12px] border-[1.5px] border-neutral-100 bg-white">
  <!-- The header holds its own controls (title link, action), so a disabled row drops the role
       outright: `aria-disabled` here would announce those controls as disabled too. -->
  <div
    class="flex min-h-[54px] w-full items-center justify-between gap-3 p-3"
    [attr.role]="isDisabled ? null : 'button'"
    [attr.tabindex]="isDisabled ? null : 0"
    [attr.aria-expanded]="isDisabled ? null : isOpen"
    [class.cursor-default]="isDisabled"
    [class.cursor-pointer]="!isDisabled"
    (click)="toggle()"
    (keydown)="onKeydown($event)"
  >
    <div class="inline-flex items-center gap-3">
      @if (showCheckbox) {
        <div class="flex items-center">
          <ui-checkbox [(ngModel)]="checked" (ngModelChange)="onMainCheckboxValueChange($event)"></ui-checkbox>
        </div>
      }
      @if (iconTemplate) {
        <ng-container [ngTemplateOutlet]="iconTemplate"></ng-container>
      }
    </div>

    <div class="min-w-0 flex-1">
      <div>
        @if (titleTemplate) {
          <ng-container [ngTemplateOutlet]="titleTemplate" [ngTemplateOutletContext]="{ disabled: !isBottomSectionEnabled }"></ng-container>
        } @else if (linkUrl) {
          <a
            class="inline-flex items-center gap-1 no-underline hover:underline"
            [ngClass]="isBottomSectionEnabled ? 'text-neutral-600' : 'text-neutral-300 pointer-events-none'"
            [attr.tabindex]="!isBottomSectionEnabled ? -1 : null"
            [attr.aria-disabled]="!isBottomSectionEnabled"
            [href]="linkUrl"
            target="_blank"
          >
            <span>{{ title }}</span>
            <ui-icon name="ExternalLink" size="16"></ui-icon>
          </a>
        } @else {
          <span [ngClass]="isBottomSectionEnabled ? 'text-neutral-600' : 'text-neutral-300'">{{ title }}</span>
        }
      </div>

      <div class="mt-0" [class.accordion-section--disabled]="!isBottomSectionEnabled">
        @if (descriptionTemplate) {
          <ng-container
            [ngTemplateOutlet]="descriptionTemplate"
            [ngTemplateOutletContext]="{ disabled: !isBottomSectionEnabled }"
          ></ng-container>
        }
      </div>
    </div>

    @if (actionTemplate) {
      <div class="shrink-0" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
        <ng-container [ngTemplateOutlet]="actionTemplate"></ng-container>
      </div>
    }

    @if (!isDisabled) {
      <ui-icon
        name="ChevronDown"
        size="24"
        class="text-neutral-500 transition-transform duration-300 ease-in-out"
        [class.rotate-180]="isOpen"
      ></ui-icon>
    }
  </div>

  <div class="accordion-content-wrapper" [class.is-open]="isOpen && !isDisabled" [attr.aria-hidden]="!(isOpen && !isDisabled)">
    <div class="accordion-content-inner px-4 pb-4">
      @if (contentTemplate) {
        <ng-container [ngTemplateOutlet]="contentTemplate"></ng-container>
      } @else {
        @if (loadingTemplate) {
          <div [class]="descriptionMarginClass">
            <ng-container [ngTemplateOutlet]="loadingTemplate"></ng-container>
          </div>
        } @else {
          @if (noticeText) {
            <!--
              Primary/400 at 8% as an 8-digit hex, not `bg-primary-400/[0.08]`: the theme colours are bare
              `var(--colors-primary-*)`, which Tailwind cannot parse, so an alpha modifier on one silently
              emits no rule at all and the box renders with no background.
            -->
            <div class="t-body text-primary-400 mb-4 rounded-lg bg-[#0F7A9114] px-3 py-2 last:mb-0" [class]="descriptionMarginClass">
              {{ noticeText }}
            </div>
          }
          @if (matchTemplate) {
            <div class="mb-4 last:mb-0" [class]="descriptionMarginClass">
              <ng-container [ngTemplateOutlet]="matchTemplate"></ng-container>
            </div>
          }
          @if (detailsMarkdown) {
            <div class="accordion-markdown" [class]="descriptionMarginClass" [class.accordion-section--disabled]="!isBottomSectionEnabled">
              <markdown [data]="renderedMarkdown"></markdown>
            </div>
          }
        }
        @if (relations.length) {
          <div class="mt-3 -mx-4 overflow-hidden">
            @if (relationsHeaderTemplate || relationsHeaderText) {
              <div class="bg-warning-100 text-warning-400 min-h-9 px-4 py-2 flex items-center gap-2 t-body">
                @if (relationsHeaderTemplate) {
                  <ng-container [ngTemplateOutlet]="relationsHeaderTemplate"></ng-container>
                } @else {
                  {{ relationsHeaderText }}
                }
              </div>
            }
            <div class="bg-white px-4 pt-4 flex flex-col gap-4">
              @for (relation of relations; track trackRelation($index, relation)) {
                <button
                  type="button"
                  class="min-h-5 self-start text-neutral-600 inline-flex items-center gap-[8px] cursor-pointer bg-transparent border-0 p-0 text-left"
                  (click)="onRelationClick(relation)"
                >
                  @if (relationTemplate) {
                    <ng-container [ngTemplateOutlet]="relationTemplate" [ngTemplateOutletContext]="{ $implicit: relation }"></ng-container>
                  } @else {
                    <ui-tooltip [tooltipMessage]="getRelationTooltip(relation) | translate" [tooltipPosition]="'above'">
                      <ui-icon [name]="getRelationIcon(relation)" size="20" class="shrink-0 text-secondary-500"></ui-icon>
                    </ui-tooltip>
                    <span class="t-body">{{ relation?.name }}</span>
                  }
                </button>
              }
            </div>
          </div>
        }
        @if (shouldRenderSources || showCommentSection) {
          <div
            class="bottom-section mt-4 pl-5 -mx-4 -mb-4 bg-neutral-100 rounded-b-[12px] border-x-[1.5px] border-b-[1.5px] border-neutral-100"
            [class.bottom-section--disabled]="!isBottomSectionEnabled"
          >
            @if (shouldRenderSources) {
              <ui-tooltip
                [tooltipMessage]="disabledSourcesHint | translate"
                [tooltipPosition]="'above'"
                [showTooltip]="!isBottomSectionEnabled"
              >
                <div class="sources-section px-4 pt-4 pb-4">
                  <div class="mb-3 flex items-center justify-between">
                    <span class="t-body text-neutral-600">{{ sourcesTitle | translate }}:</span>
                    <button
                      type="button"
                      class="t-body cursor-pointer border-0 bg-transparent p-0 text-informative-400"
                      (click)="onToggleAllSources()"
                    >
                      {{
                        (areAllSourcesChecked ? "add-document-slider.Uncheck_All_Sources" : "add-document-slider.Check_All_Sources")
                          | translate
                      }}
                    </button>
                  </div>
                  <div class="flex flex-col gap-2">
                    @for (index of sortedSourceIndices; track trackSource(index, sources[index])) {
                      @let source = sources[index];
                      <div class="source-row flex gap-2 min-w-0 items-start">
                        <div class="flex shrink-0 items-center pt-[2px]">
                          <ui-checkbox
                            [ngModel]="!!source?.[sourceCheckedKey]"
                            (ngModelChange)="onSourceCheckboxValueChange(index, $event)"
                          ></ui-checkbox>
                        </div>

                        <div class="flex min-w-0 flex-1 gap-1 items-start">
                          <span class="shrink-0 text-neutral-600">[{{ getSourceNumber(index) }}]</span>
                          @if (source?.[sourceUrlKey]) {
                            <a
                              [href]="source[sourceUrlKey]"
                              target="_blank"
                              class="source-link text-neutral-600 underline min-w-0"
                              [ngClass]="!isBottomSectionEnabled ? 'pointer-events-none' : ''"
                              [attr.tabindex]="!isBottomSectionEnabled ? 0 : -1"
                              (click)="onSourceLinkClick($event)"
                              >{{ source?.[sourceLabelKey] || source?.[sourceUrlKey] }}</a
                            >
                          } @else {
                            <span class="t-body text-neutral-600">{{ source?.[sourceLabelKey] || "-" }}</span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </ui-tooltip>
            }
            @if (showCommentSection) {
              <div class="min-h-[138px] w-full px-4 pt-3 pb-4">
                <div class="mb-2 t-body text-neutral-400">
                  {{ commentTitle | translate }}
                </div>
                <ui-tooltip
                  [tooltipMessage]="disabledCommentHint | translate"
                  [tooltipPosition]="'above'"
                  [showTooltip]="!shouldEnableCommentInput"
                >
                  <div
                    class="relative min-h-16 w-full rounded-lg border border-neutral-200 px-3 py-2"
                    [class.bg-neutral-100]="!shouldEnableCommentInput"
                    [class.bg-white]="shouldEnableCommentInput"
                  >
                    <textarea
                      class="min-h-[64px] w-full max-h-[180px] overflow-auto border-0 bg-transparent py-1 pr-2 pl-2 text-neutral-500 t-body"
                      [class.resize-y]="shouldEnableCommentInput"
                      [class.resize-none]="!shouldEnableCommentInput"
                      [value]="commentText"
                      [attr.maxLength]="commentMaxLength"
                      [placeholder]="commentPlaceholder | translate"
                      [disabled]="!shouldEnableCommentInput"
                      (input)="onCommentChange($event)"
                      (focus)="onCommentFocus()"
                      (blur)="onCommentBlur()"
                    ></textarea>
                  </div>
                </ui-tooltip>

                <div class="mt-2 t-cap text-neutral-400" *ngIf="isCommentInputFocused || commentText">
                  <span>{{ remainingCommentCharacters }} {{ "add-document-slider.Characters_Remaining" | translate }}</span>
                </div>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>
</div>
